import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../hooks/useToast';
import { supabase } from '../../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  GripVertical,
  ChevronDown,
  BookOpen,
  Play,
  FileText,
  Download,
  Save,
  HelpCircle,
  Settings
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { QuizBuilder } from '../../../components/admin/quiz/QuizBuilder';
import { MediaSelector } from '../../../components/media/MediaSelector';

interface Module {
  id: string;
  title: string;
  description: string;
  sort_order: number;
  is_published: boolean;
  lessons: Lesson[];
}

interface Lesson {
  id: string;
  title: string;
  content: string;
  content_type: 'video' | 'text' | 'quiz' | 'assignment' | 'file';
  video_url?: string;
  video_duration: number;
  file_url?: string;
  file_name?: string;
  sort_order: number;
  is_free: boolean;
  is_published: boolean;
}

const CourseBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: courseId } = useParams();
  console.log('CourseBuilderPage - Paramètre récupéré de l\'URL:', useParams());
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const [modules, setModules] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [isModuleDialogOpen, setIsModuleDialogOpen] = useState(false);
  const [isLessonDialogOpen, setIsLessonDialogOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedModule, setDraggedModule] = useState<string | null>(null);
  const [draggedLesson, setDraggedLesson] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [dragOverModule, setDragOverModule] = useState<string | null>(null);
  const [dragOverLesson, setDragOverLesson] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [collapsedModules, setCollapsedModules] = useState<string[]>([]);
  const [course, setCourse] = useState<{ slug: string; title: string } | null>(null);
  const [isQuizBuilderOpen, setIsQuizBuilderOpen] = useState(false);
  const [selectedLessonForQuiz, setSelectedLessonForQuiz] = useState<string | null>(null);
  const [showVideoSelector, setShowVideoSelector] = useState(false);
  const [showFileSelector, setShowFileSelector] = useState(false);

  // Formulaires
  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: '',
    is_published: true
  });

  const [lessonForm, setLessonForm] = useState({
    title: '',
    content: '',
    content_type: 'text' as const,
    video_url: '',
    video_duration: 0,
    file_url: '',
    file_name: '',
    is_free: false,
    is_published: true
  });

  useEffect(() => {
    const { tenantId } = getDomainFilterParams();
    console.log('CourseBuilderPage - useEffect déclenché avec courseId:', courseId, 'et tenant_id:', tenantId);
    loadCourseContent();
    loadCourseData();
  }, [courseId]);

  const loadCourseContent = async () => {
    const { tenantId } = getDomainFilterParams();
    if (!courseId || !tenantId) return;
    
    console.log('CourseBuilderPage - Chargement des modules pour courseId:', courseId);
    console.log('CourseBuilderPage - tenant_id:', tenantId);
    
    setIsLoading(true);
    try {
      // Charger les modules du cours depuis Supabase
      // Requête sans filtre tenant_id pour tester
      console.log('CourseBuilderPage - Tentative de requête sans filtres restrictifs');
      const { data: modulesData, error: modulesError } = await supabase
        .from('elearn_modules')
        .select('*')
        .eq('course_id', courseId)
        // Filtre tenant_id temporairement désactivé pour test
        .order('sort_order', { ascending: true });

      console.log('CourseBuilderPage - Résultat requête modules:', { modulesData, modulesError });

      if (modulesError) throw modulesError;

      // Suppression temporaire de la logique de données simulées
      if (!modulesData || modulesData.length === 0) {
        console.log('CourseBuilderPage - Aucun module trouvé pour ce cours');
        setModules([]);
      } else {
        console.log('CourseBuilderPage - Modules trouvés:', modulesData.length);
        // Pour chaque module, charger ses leçons
        const modulesWithLessons = await Promise.all(
          modulesData.map(async (module) => {
            const { data: lessonsData, error: lessonsError } = await supabase
              .from('elearn_lessons')
              .select('*')
              .eq('module_id', module.id)
              .order('sort_order', { ascending: true });

            console.log(`CourseBuilderPage - Leçons pour module ${module.id}:`, { lessonsData, lessonsError });

            if (lessonsError) throw lessonsError;

            return {
              ...module,
              lessons: lessonsData || []
            };
          })
        );

        console.log('CourseBuilderPage - Modules avec leçons:', modulesWithLessons);
        
        // Forcer l'affichage des modules récupérés
        if (modulesWithLessons && modulesWithLessons.length > 0) {
          console.log('CourseBuilderPage - Mise à jour de l\'état avec', modulesWithLessons.length, 'modules');
          setModules([...modulesWithLessons]);
          
          // Vérifier après la mise à jour
          setTimeout(() => {
            console.log('CourseBuilderPage - Vérification de l\'état modules après mise à jour:', modules.length);
          }, 100);
        } else {
          console.log('CourseBuilderPage - Aucun module à afficher');
          setModules([]);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger le contenu du cours',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCourseData = async () => {
    const { tenantId } = getDomainFilterParams();
    if (!courseId || !tenantId) return;
    try {
      console.log('CourseBuilderPage - Chargement des données du cours:', courseId);
      console.log('CourseBuilderPage - tenant_id:', tenantId);
      
      let courseQuery = supabase
        .from('elearn_courses')
        .select('slug, title')
        .eq('id', courseId);
      
      courseQuery = applyDomainFilterToQuery(courseQuery);
      const { data, error } = await courseQuery.single();
      
      console.log('CourseBuilderPage - Résultat requête cours:', { data, error });
      
      if (error) throw error;
      setCourse(data);
    } catch (error) {
      console.error('Erreur lors du chargement des données du cours:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les informations du cours.',
        variant: 'destructive'
      });
    }
  };

  const addModule = async () => {
    const { tenantId, domain } = getDomainFilterParams();
    if (!courseId || !tenantId) return;
    
    try {
      const newModuleId = uuidv4();
      const newModule: Module = {
        id: newModuleId,
        title: moduleForm.title,
        description: moduleForm.description,
        sort_order: modules.length + 1,
        is_published: moduleForm.is_published,
        lessons: []
      };

      // Ajouter le module dans Supabase
      const { error } = await supabase
        .from('elearn_modules')
        .insert({
          id: newModuleId,
          course_id: courseId,
          tenant_id: tenantId,
          domain: domain,
          title: moduleForm.title,
          description: moduleForm.description,
          sort_order: modules.length + 1,
          is_published: moduleForm.is_published,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      // Mettre à jour l'état local
      setModules([...modules, newModule]);
      setModuleForm({ title: '', description: '', is_published: true });
      setIsModuleDialogOpen(false);
      
      toast({
        title: 'Module créé',
        description: `Le module "${newModule.title}" a été créé avec succès.`
      });
    } catch (error) {
      console.error('Erreur lors de la création du module:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de créer le module',
        variant: 'destructive'
      });
    }
  };

  const editLesson = async (moduleId: string, lessonId: string) => {
    const { tenantId } = getDomainFilterParams();
    if (!courseId || !tenantId) return;
    
    try {
      // Trouver la leçon à éditer
      const targetModule = modules.find(m => m.id === moduleId);
      if (!targetModule) return;
      
      const targetLesson = targetModule.lessons.find(l => l.id === lessonId);
      if (!targetLesson) return;
      
      // Mettre à jour la leçon dans Supabase
      const { error } = await supabase
        .from('elearn_lessons')
        .update({
          title: lessonForm.title,
          content: lessonForm.content,
          content_type: lessonForm.content_type,
          video_url: lessonForm.video_url || '',
          video_duration: lessonForm.video_duration || 0,
          file_url: lessonForm.file_url || '',
          file_name: lessonForm.file_name || '',
          is_free: lessonForm.is_free,
          is_published: lessonForm.is_published,
          updated_at: new Date().toISOString()
        })
        .eq('id', lessonId);

      if (error) throw error;

      // Mettre à jour l'état local
      const updatedModules = modules.map(module => {
        if (module.id === moduleId) {
          return {
            ...module,
            lessons: module.lessons.map(lesson => {
              if (lesson.id === lessonId) {
                return {
                  ...lesson,
                  title: lessonForm.title,
                  content: lessonForm.content,
                  content_type: lessonForm.content_type,
                  video_url: lessonForm.video_url || '',
                  video_duration: lessonForm.video_duration || 0,
                  file_url: lessonForm.file_url || '',
                  file_name: lessonForm.file_name || '',
                  is_free: lessonForm.is_free,
                  is_published: lessonForm.is_published
                };
              }
              return lesson;
            })
          };
        }
        return module;
      });

      setModules(updatedModules);
      setLessonForm({
        title: '',
        content: '',
        content_type: 'text' as const,
        video_url: '',
        video_duration: 0,
        file_url: '',
        file_name: '',
        is_free: false,
        is_published: true
      });
      setIsLessonDialogOpen(false);
      setIsEditMode(false);
      
      toast({
        title: 'Leçon modifiée',
        description: `La leçon "${lessonForm.title}" a été modifiée avec succès.`
      });
    } catch (error) {
      console.error('Erreur lors de la modification de la leçon:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier la leçon',
        variant: 'destructive'
      });
    }
  };

  // Fonctions de drag and drop pour les modules
  const handleModuleDragStart = (e: React.DragEvent, moduleId: string) => {
    setDraggedModule(moduleId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleModuleDragOver = (e: React.DragEvent, moduleId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverModule(moduleId);
  };

  const handleModuleDragLeave = () => {
    setDragOverModule(null);
  };

  const handleModuleDrop = async (e: React.DragEvent, targetModuleId: string) => {
    e.preventDefault();
    if (!draggedModule || draggedModule === targetModuleId) return;

    const draggedIndex = modules.findIndex(m => m.id === draggedModule);
    const targetIndex = modules.findIndex(m => m.id === targetModuleId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Réorganiser les modules
    const newModules = [...modules];
    const [draggedModuleObj] = newModules.splice(draggedIndex, 1);
    newModules.splice(targetIndex, 0, draggedModuleObj);

    // Mettre à jour les sort_order
    const updatedModules = newModules.map((module, index) => ({
      ...module,
      sort_order: index + 1
    }));

    setModules(updatedModules);
    setDraggedModule(null);
    setDragOverModule(null);

    // Sauvegarder l'ordre dans la base de données
    try {
      for (const module of updatedModules) {
        await supabase
          .from('elearn_modules')
          .update({ sort_order: module.sort_order })
          .eq('id', module.id);
      }
      toast({
        title: 'Ordre mis à jour',
        description: 'L\'ordre des modules a été sauvegardé.'
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'ordre:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder l\'ordre des modules',
        variant: 'destructive'
      });
    }
  };

  // Fonctions de drag and drop pour les leçons
  const handleLessonDragStart = (e: React.DragEvent, moduleId: string, lessonId: string) => {
    setDraggedLesson({ moduleId, lessonId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleLessonDragOver = (e: React.DragEvent, moduleId: string, lessonId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverLesson({ moduleId, lessonId });
  };

  const handleLessonDragLeave = () => {
    setDragOverLesson(null);
  };

  const handleLessonDrop = async (e: React.DragEvent, targetModuleId: string, targetLessonId: string) => {
    e.preventDefault();
    console.log('handleLessonDrop appelé:', { draggedLesson, targetModuleId, targetLessonId });
    
    if (!draggedLesson) {
      console.log('Aucune leçon en cours de glissement');
      return;
    }
    
    if (draggedLesson.moduleId === targetModuleId && draggedLesson.lessonId === targetLessonId) {
      console.log('Même leçon source et cible');
      return;
    }

    const sourceModule = modules.find(m => m.id === draggedLesson.moduleId);
    const targetModule = modules.find(m => m.id === targetModuleId);

    if (!sourceModule || !targetModule) {
      console.log('Module source ou cible non trouvé');
      return;
    }

    const draggedLessonObj = sourceModule.lessons.find(l => l.id === draggedLesson.lessonId);
    if (!draggedLessonObj) {
      console.log('Leçon source non trouvée');
      return;
    }

    console.log('Déplacement de la leçon:', { 
      de: `${sourceModule.title} (${draggedLesson.moduleId})`, 
      vers: `${targetModule.title} (${targetModuleId})`,
      leçon: draggedLessonObj.title
    });

    // Créer une copie des modules
    const newModules = modules.map(module => ({
      ...module,
      lessons: [...module.lessons]
    }));

    // Retirer la leçon de son module source
    const sourceModuleIndex = newModules.findIndex(m => m.id === draggedLesson.moduleId);
    const draggedLessonIndex = newModules[sourceModuleIndex].lessons.findIndex(l => l.id === draggedLesson.lessonId);
    const [removedLesson] = newModules[sourceModuleIndex].lessons.splice(draggedLessonIndex, 1);
    
    // Ajouter la leçon au module cible
    const targetModuleIndex = newModules.findIndex(m => m.id === targetModuleId);
    const targetLessonIndex = newModules[targetModuleIndex].lessons.findIndex(l => l.id === targetLessonId);
    
    // Si targetLessonIndex est -1, ajouter à la fin du module
    const insertIndex = targetLessonIndex === -1 ? newModules[targetModuleIndex].lessons.length : targetLessonIndex;
    
    newModules[targetModuleIndex].lessons.splice(insertIndex, 0, {
      ...removedLesson,
      module_id: targetModuleId
    });

    // Mettre à jour les sort_order pour tous les modules affectés
    newModules.forEach(module => {
      module.lessons = module.lessons.map((lesson, index) => ({
        ...lesson,
        sort_order: index + 1
      }));
    });

    console.log('Modules mis à jour:', newModules);
    setModules(newModules);
    setDraggedLesson(null);
    setDragOverLesson(null);

    // Sauvegarder dans la base de données
    try {
      console.log('Mise à jour dans Supabase...');
      // Mettre à jour la leçon déplacée
      const { error: updateError } = await supabase
        .from('elearn_lessons')
        .update({ 
          module_id: targetModuleId,
          sort_order: insertIndex + 1
        })
        .eq('id', draggedLesson.lessonId);
        
      if (updateError) {
        throw updateError;
      }

      // Mettre à jour l'ordre des autres leçons dans les modules affectés
      for (const module of newModules) {
        if (module.id === draggedLesson.moduleId || module.id === targetModuleId) {
          for (const lesson of module.lessons) {
            const { error: sortError } = await supabase
              .from('elearn_lessons')
              .update({ sort_order: lesson.sort_order })
              .eq('id', lesson.id);
              
            if (sortError) {
              console.error('Erreur lors de la mise à jour de l\'ordre:', sortError);
            }
          }
        }
      }

      toast({
        title: 'Leçon déplacée',
        description: 'La leçon a été déplacée avec succès.'
      });
    } catch (error) {
      console.error('Erreur lors du déplacement de la leçon:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de déplacer la leçon',
        variant: 'destructive'
      });
      // Recharger les données en cas d'erreur
      loadCourseContent();
    }
  };

  const toggleModuleCollapse = (moduleId: string) => {
    setCollapsedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId) 
        : [...prev, moduleId]
    );
  };

  const openQuizBuilder = (lessonId: string) => {
    setSelectedLessonForQuiz(lessonId);
    setIsQuizBuilderOpen(true);
  };

  const closeQuizBuilder = () => {
    setIsQuizBuilderOpen(false);
    setSelectedLessonForQuiz(null);
  };

  const addLesson = async (moduleId: string) => {
    const { tenantId, domain } = getDomainFilterParams();
    if (!courseId || !tenantId) return;
    
    try {
      const newLessonId = uuidv4();
      const targetModule = modules.find(m => m.id === moduleId);
      if (!targetModule) return;
      
      const newLesson: Lesson = {
        id: newLessonId,
        title: lessonForm.title,
        content: lessonForm.content,
        content_type: lessonForm.content_type,
        video_url: lessonForm.video_url,
        video_duration: lessonForm.video_duration,
        file_url: lessonForm.file_url,
        file_name: lessonForm.file_name,
        sort_order: targetModule.lessons.length + 1,
        is_free: lessonForm.is_free,
        is_published: lessonForm.is_published
      };

      // Ajouter la leçon dans Supabase
      const { error } = await supabase
        .from('elearn_lessons')
        .insert({
          id: newLessonId,
          module_id: moduleId,
          tenant_id: tenantId,
          domain: domain,
          title: lessonForm.title,
          content: lessonForm.content,
          content_type: lessonForm.content_type,
          video_url: lessonForm.video_url,
          video_duration: lessonForm.video_duration,
          file_url: lessonForm.file_url,
          file_name: lessonForm.file_name,
          file_size: 0, // Valeur par défaut
          sort_order: targetModule.lessons.length + 1,
          is_free: lessonForm.is_free,
          is_published: lessonForm.is_published,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      // Mettre à jour l'état local
      const updatedModules = modules.map(module => {
        if (module.id === moduleId) {
          return {
            ...module,
            lessons: [...module.lessons, newLesson]
          };
        }
        return module;
      });

      setModules(updatedModules);
      setLessonForm({
        title: '',
        content: '',
        content_type: 'text' as const,
        video_url: '',
        video_duration: 0,
        file_url: '',
        file_name: '',
        is_free: false,
        is_published: true
      });
      setIsLessonDialogOpen(false);
      
      toast({
        title: 'Leçon créée',
        description: `La leçon "${newLesson.title}" a été créée avec succès.`
      });
    } catch (error) {
      console.error('Erreur lors de la création de la leçon:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de créer la leçon',
        variant: 'destructive'
      });
    }
  };

  const deleteModule = async (moduleId: string) => {
    const { tenantId } = getDomainFilterParams();
    if (!courseId || !tenantId) return;
    
    const moduleToDelete = modules.find(m => m.id === moduleId);
    if (!moduleToDelete) return;
    
    // Demander confirmation avant suppression
    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le module "${moduleToDelete.title}" ?\n` +
      `Cette action supprimera également toutes les leçons qu'il contient (${moduleToDelete.lessons.length} leçon(s)).\n` +
      `Cette action est irréversible.`
    );
    
    if (!confirmDelete) return;
    
    try {
      // Supprimer d'abord toutes les leçons du module
      const { error: lessonsError } = await supabase
        .from('elearn_lessons')
        .delete()
        .eq('module_id', moduleId);

      if (lessonsError) throw lessonsError;

      // Puis supprimer le module
      const { error } = await supabase
        .from('elearn_modules')
        .delete()
        .eq('id', moduleId);

      if (error) throw error;

      // Mettre à jour l'état local
      setModules(prev => prev.filter(m => m.id !== moduleId));
      
      toast({
        title: 'Module supprimé',
        description: `Le module "${moduleToDelete.title}" a été supprimé avec succès.`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error deleting module:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la suppression du module.',
        variant: 'destructive'
      });
    }
  };

  const deleteLesson = async (moduleId: string, lessonId: string) => {
    const { tenantId } = getDomainFilterParams();
    if (!courseId || !tenantId) return;
    
    // Trouver la leçon à supprimer pour afficher son titre dans la confirmation
    const module = modules.find(m => m.id === moduleId);
    if (!module) return;
    
    const lessonToDelete = module.lessons.find(l => l.id === lessonId);
    if (!lessonToDelete) return;
    
    // Demander confirmation avant suppression
    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer la leçon "${lessonToDelete.title}" ?\n` +
      `Cette action est irréversible.`
    );
    
    if (!confirmDelete) return;
    
    try {
      const { error } = await supabase
        .from('elearn_lessons')
        .delete()
        .eq('id', lessonId);

      if (error) throw error;

      // Mettre à jour l'état local
      setModules(prev => 
        prev.map(module => 
          module.id === moduleId
            ? { 
                ...module, 
                lessons: module.lessons.filter(lesson => lesson.id !== lessonId) 
              }
            : module
        )
      );
      
      toast({
        title: 'Leçon supprimée',
        description: `La leçon "${lessonToDelete.title}" a été supprimée avec succès.`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la suppression de la leçon.',
        variant: 'destructive'
      });
    }
  };

  const saveAllChanges = async () => {
    console.log('Fonction saveAllChanges appelée');
    console.log('courseId:', courseId);
    console.log('tenant:', tenant);
    console.log('modules:', modules);
    
    const { tenantId, domain } = getDomainFilterParams();
    if (!courseId || !tenantId) {
      console.error('Erreur: courseId ou tenantId manquant');
      toast({
        title: 'Erreur',
        description: 'Impossible d\'enregistrer: informations du cours manquantes',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      // Indiquer que la sauvegarde est en cours
      setIsSaving(true);
      
      // Afficher un toast de chargement
      toast({
        title: 'Sauvegarde en cours',
        description: 'Enregistrement de tous les modules et leçons...',
        variant: 'default'
      });
      
      console.log('Début de la sauvegarde des modules et leçons');
      
      // Pour chaque module, mettre à jour les données dans Supabase
      for (const module of modules) {
        console.log('Sauvegarde du module:', module.id, module.title);
        
        const { data: moduleData, error: moduleError } = await supabase
          .from('elearn_modules')
          .update({
            title: module.title,
            description: module.description,
            is_published: module.is_published,
            sort_order: module.sort_order,
            updated_at: new Date().toISOString()
          })
          .eq('id', module.id)
          .select();
        
        console.log('Résultat de la mise à jour du module:', { moduleData, moduleError });
        
        if (moduleError) {
          console.error('Erreur lors de la mise à jour du module:', moduleError);
          throw moduleError;
        }
        
        // Pour chaque leçon du module, mettre à jour les données
        for (const lesson of module.lessons) {
          console.log('Sauvegarde de la leçon:', lesson.id, lesson.title);
          
          const { data: lessonData, error: lessonError } = await supabase
            .from('elearn_lessons')
            .update({
              title: lesson.title,
              content: lesson.content,
              content_type: lesson.content_type,
              video_url: lesson.video_url || '',
              video_duration: lesson.video_duration || 0,
              file_url: lesson.file_url || '',
              file_name: lesson.file_name || '',
              is_free: lesson.is_free,
              is_published: lesson.is_published,
              sort_order: lesson.sort_order,
              updated_at: new Date().toISOString()
            })
            .eq('id', lesson.id)
            .select();
          
          console.log('Résultat de la mise à jour de la leçon:', { lessonData, lessonError });
          
          if (lessonError) {
            console.error('Erreur lors de la mise à jour de la leçon:', lessonError);
            throw lessonError;
          }
        }
      }
      
      console.log('Sauvegarde terminée avec succès');
      
      // Mettre à jour la date de dernière sauvegarde
      const maintenant = new Date();
      setLastSaved(maintenant.toLocaleTimeString());
      
      // Afficher un toast de succès
      toast({
        title: 'Sauvegarde réussie',
        description: 'Tous les modules et leçons ont été enregistrés avec succès.',
        variant: 'default'
      });
      
      // Recharger les données pour s'assurer que tout est à jour
      await loadCourseContent();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde globale:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'enregistrer toutes les modifications',
        variant: 'destructive'
      });
    } finally {
      // Désactiver l'indicateur de sauvegarde
      setIsSaving(false);
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Play size={16} className="text-red-400" />;
      case 'text': return <FileText size={16} className="text-blue-400" />;
      case 'quiz': return <HelpCircle size={16} className="text-purple-400" />;
      case 'assignment': return <Edit size={16} className="text-yellow-400" />;
      case 'file': return <Download size={16} className="text-green-400" />;
      default: return <BookOpen size={16} className="text-white/50" />;
    }
  };

  const getContentTypeLabel = (type: string) => {
    const types = {
      video: 'Vidéo',
      text: 'Texte',
      quiz: 'Quiz',
      assignment: 'Exercice',
      file: 'Fichier'
    };
    return types[type as keyof typeof types] || type;
  };

  return (
    <AdminLayout>
      {/* Dialog pour créer/éditer un module */}
      <Dialog open={isModuleDialogOpen} onOpenChange={setIsModuleDialogOpen}>
        <DialogContent className="bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Modifier le module' : 'Nouveau module'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="module-title">Titre du module</Label>
              <Input
                id="module-title"
                value={moduleForm.title}
                onChange={(e) => setModuleForm({...moduleForm, title: e.target.value})}
                className="bg-black/30 border-white/20 text-white"
                placeholder="Ex: Introduction au développement web"
              />
            </div>
            <div>
              <Label htmlFor="module-description">Description</Label>
              <Textarea
                id="module-description"
                value={moduleForm.description}
                onChange={(e) => setModuleForm({...moduleForm, description: e.target.value})}
                className="bg-black/30 border-white/20 text-white"
                placeholder="Décrivez brièvement le contenu de ce module"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="module-published"
                checked={moduleForm.is_published}
                onCheckedChange={(checked) => setModuleForm({...moduleForm, is_published: checked})}
              />
              <Label htmlFor="module-published">Publié</Label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={isEditMode ? () => {} /* handleEditModule */ : addModule} className="bg-red-600 hover:bg-red-700 text-white">
              {isEditMode ? 'Enregistrer les modifications' : 'Créer le module'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog pour créer/éditer une leçon */}
      <Dialog open={isLessonDialogOpen} onOpenChange={(isOpen) => {
        if (!isOpen) {
          setSelectedModuleId(null);
          setSelectedLesson(null);
          setIsEditMode(false);
        }
        setIsLessonDialogOpen(isOpen);
      }}>
        <DialogContent className="bg-black/90 border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 
                `Modifier la leçon "${selectedLesson?.title || ''}"` : 
                `Nouvelle leçon pour le module "${modules.find(m => m.id === selectedModuleId)?.title || ''}"`
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="lesson-title">Titre de la leçon</Label>
              <Input
                id="lesson-title"
                value={lessonForm.title}
                onChange={(e) => setLessonForm({...lessonForm, title: e.target.value})}
                className="bg-black/30 border-white/20 text-white"
                placeholder="Ex: Les bases du HTML"
              />
            </div>
            <div>
              <Label htmlFor="lesson-type">Type de contenu</Label>
              <Select 
                value={lessonForm.content_type} 
                onValueChange={(value: any) => setLessonForm({...lessonForm, content_type: value})}
              >
                <SelectTrigger className="bg-black/30 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">Vidéo</SelectItem>
                  <SelectItem value="text">Texte</SelectItem>
                  <SelectItem value="quiz">Quiz</SelectItem>
                  <SelectItem value="assignment">Exercice</SelectItem>
                  <SelectItem value="file">Fichier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {lessonForm.content_type === 'video' && (
              <div className="space-y-2">
                <Label htmlFor="video-url">URL de la vidéo</Label>
                <div className="flex gap-2">
                  <Input
                    id="video-url"
                    value={lessonForm.video_url}
                    onChange={(e) => setLessonForm({...lessonForm, video_url: e.target.value})}
                    className="bg-black/30 border-white/20 text-white flex-1"
                    placeholder="https://example.com/video.mp4"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowVideoSelector(true)}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                  >
                    Médiathèque
                  </Button>
                </div>
              </div>
            )}
            {lessonForm.content_type === 'file' && (
              <div className="space-y-2">
                <Label htmlFor="file-url">URL du fichier</Label>
                <div className="flex gap-2">
                  <Input
                    id="file-url"
                    value={lessonForm.file_url}
                    onChange={(e) => setLessonForm({...lessonForm, file_url: e.target.value})}
                    className="bg-black/30 border-white/20 text-white flex-1"
                    placeholder="https://example.com/document.pdf"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowFileSelector(true)}
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                  >
                    Médiathèque
                  </Button>
                </div>
                <div>
                  <Label htmlFor="file-name">Nom du fichier</Label>
                  <Input
                    id="file-name"
                    value={lessonForm.file_name}
                    onChange={(e) => setLessonForm({...lessonForm, file_name: e.target.value})}
                    className="bg-black/30 border-white/20 text-white"
                    placeholder="document.pdf"
                  />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="lesson-content">Contenu</Label>
              <Textarea
                id="lesson-content"
                value={lessonForm.content}
                onChange={(e) => setLessonForm({...lessonForm, content: e.target.value})}
                className="bg-black/30 border-white/20 text-white"
                placeholder="Contenu de la leçon..."
                rows={4}
              />
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={lessonForm.is_free}
                  onCheckedChange={(checked) => setLessonForm({...lessonForm, is_free: checked})}
                />
                <Label>Leçon gratuite</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={lessonForm.is_published}
                  onCheckedChange={(checked) => setLessonForm({...lessonForm, is_published: checked})}
                />
                <Label>Leçon publiée</Label>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => {
                setIsLessonDialogOpen(false);
                setIsEditMode(false);
                setSelectedLesson(null);
              }}>
                Annuler
              </Button>
              <Button 
                onClick={() => {
                  if (isEditMode && selectedLesson && selectedModuleId) {
                    editLesson(selectedModuleId, selectedLesson.id);
                  } else if (selectedModuleId) {
                    addLesson(selectedModuleId);
                  }
                }}
                className="bg-red-600 hover:bg-red-700"
                disabled={!selectedModuleId}
              >
                {isEditMode ? 'Modifier la leçon' : 'Créer la leçon'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="container mx-auto py-8 px-4 space-y-6">
        {/* Header de la page */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/elearning/courses')}
            className="border-white/20 text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à la liste
          </Button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Constructeur de cours: <span className="text-red-500">{course?.title || ''}</span>
              </h1>
              <p className="text-white/70 mt-1">Organisez le contenu de votre cours en modules et leçons.</p>
            </div>
            {/* Boutons d'action supprimés de la vue mobile car déjà présents dans la barre latérale */}
          </div>
        </div>

        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Colonne principale : Modules et leçons (à gauche) */}
          <div className="lg:col-span-3 space-y-6">
            <Card className="bg-black/20 border-white/10">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-white">Modules du cours</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {modules.map((module) => (
                  <motion.div
                    key={module.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ 
                      opacity: draggedModule === module.id ? 0.5 : 1, 
                      y: 0,
                      scale: draggedModule === module.id ? 0.95 : 1,
                      rotateZ: draggedModule === module.id ? 2 : 0
                    }}
                    whileHover={{ scale: 1.02 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 300, 
                      damping: 30,
                      opacity: { duration: 0.2 },
                      scale: { duration: 0.2 }
                    }}
                    className={`bg-white/5 border rounded-lg p-4 transition-all duration-200 ${
                      draggedModule === module.id 
                        ? 'border-blue-400/50 shadow-lg shadow-blue-500/20' 
                        : dragOverModule === module.id 
                        ? 'border-green-400/50 bg-green-500/10 shadow-lg shadow-green-500/20' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    draggable
                    onDragStart={(e) => handleModuleDragStart(e, module.id)}
                    onDragOver={(e) => handleModuleDragOver(e, module.id)}
                    onDragLeave={handleModuleDragLeave}
                    onDrop={(e) => handleModuleDrop(e, module.id)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3 cursor-pointer" onClick={() => toggleModuleCollapse(module.id)}>
                        <motion.div whileHover={{ scale: 1.2, rotate: 5 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
                          <GripVertical 
                            className={`cursor-move transition-all duration-200 ${
                              draggedModule === module.id 
                                ? 'text-blue-400' 
                                : 'text-white/50 hover:text-white'
                            }`}
                            size={16} 
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        </motion.div>
                        <motion.div animate={{ rotate: collapsedModules.includes(module.id) ? -90 : 0 }}>
                          <ChevronDown size={20} className="text-white/70" />
                        </motion.div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{module.title}</h3>
                          <p className="text-sm text-white/60">{module.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {!module.is_published && <Badge variant="secondary">Brouillon</Badge>}
                        <Button variant="ghost" size="sm" className="text-white/70 hover:text-white">
                          <Edit size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteModule(module.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    <AnimatePresence>
                      {!collapsedModules.includes(module.id) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-2 pl-7 pt-4">
                            {module.lessons.map((lesson) => (
                              <motion.div
                                key={lesson.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ 
                                  opacity: draggedLesson?.lessonId === lesson.id ? 0.5 : 1, 
                                  x: 0,
                                  scale: draggedLesson?.lessonId === lesson.id ? 0.95 : 1,
                                  rotateZ: draggedLesson?.lessonId === lesson.id ? 1 : 0
                                }}
                                whileHover={{ scale: 1.02, x: 4 }}
                                transition={{ type: "spring", stiffness: 400, damping: 25, opacity: { duration: 0.15 }, scale: { duration: 0.15 }}}
                                className={`flex items-center justify-between p-3 rounded border transition-all duration-200 cursor-move ${
                                  draggedLesson?.lessonId === lesson.id 
                                    ? 'bg-blue-500/20 border-blue-400/50 shadow-md shadow-blue-500/20' 
                                    : dragOverLesson?.lessonId === lesson.id 
                                    ? 'bg-green-500/20 border-green-400/50 shadow-md shadow-green-500/20' 
                                    : 'bg-black/20 border-white/5 hover:bg-black/30 hover:border-white/10'
                                }`}
                                draggable
                                onDragStart={(e: React.DragEvent) => handleLessonDragStart(e, module.id, lesson.id)}
                                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; handleLessonDragOver(e, module.id, lesson.id); }}
                                onDragLeave={handleLessonDragLeave}
                                onDrop={(e) => { e.stopPropagation(); handleLessonDrop(e, module.id, lesson.id); }}
                              >
                                <div className="flex items-center space-x-3">
                                  <motion.div whileHover={{ scale: 1.2, rotate: 5 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
                                    <GripVertical className={`cursor-move transition-all duration-200 ${draggedLesson?.lessonId === lesson.id ? 'text-blue-400' : 'text-white/30 hover:text-white/60'}`} size={14} />
                                  </motion.div>
                                  {getContentTypeIcon(lesson.content_type)}
                                  <div>
                                    <div className="font-medium text-white">{lesson.title}</div>
                                    <div className="text-xs text-white/50">
                                      {getContentTypeLabel(lesson.content_type)}
                                      {lesson.is_free && <Badge className="ml-2 text-xs bg-green-500/20 text-green-400">Gratuit</Badge>}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {lesson.content_type === 'quiz' && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-purple-400 hover:text-purple-300" 
                                      onClick={() => openQuizBuilder(lesson.id)}
                                      title="Gérer le quiz"
                                    >
                                      <Settings size={12} />
                                    </Button>
                                  )}
                                  <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => { setLessonForm({ title: lesson.title, content: lesson.content, content_type: lesson.content_type, video_url: lesson.video_url || '', video_duration: lesson.video_duration || 0, file_url: lesson.file_url || '', file_name: lesson.file_name || '', is_free: lesson.is_free, is_published: lesson.is_published }); setSelectedModuleId(module.id); setSelectedLesson(lesson); setIsEditMode(true); setIsLessonDialogOpen(true); }}>
                                    <Edit size={12} />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteLesson(module.id, lesson.id)} className="text-red-400 hover:text-red-300">
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                              </motion.div>
                            ))}
                            <Button variant="outline" size="sm" className="w-full border-dashed border-white/20 text-white/70 hover:text-white hover:bg-white/5" onClick={() => { setSelectedModuleId(module.id); setLessonForm({ title: '', content_type: 'text', video_url: '', content: '', is_free: false, is_published: true }); setIsLessonDialogOpen(true); }}>
                              <Plus className="mr-2" size={14} />
                              Ajouter une leçon
                            </Button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
                {modules.length === 0 && (
                  <div className="text-center py-12">
                    <BookOpen size={48} className="mx-auto text-white/30 mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">Aucun module affiché</h3>
                    <p className="text-white/60 mb-4">
                      {isLoading ? 'Chargement des modules...' : 'Commencez par créer votre premier module de cours.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Colonne des actions (à droite) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Actions principales */}
              <Card className="bg-black/20 border-white/10">
                <CardHeader><CardTitle className="text-white">Actions</CardTitle></CardHeader>
                <CardContent className="flex flex-col space-y-2">
                  <Button onClick={() => { setIsModuleDialogOpen(true); setIsEditMode(false); setModuleForm({ title: '', description: '', is_published: true }); }} className="w-full bg-red-500 hover:bg-red-600 text-white">
                    <Plus className="mr-2" size={16} />
                    Ajouter un module
                  </Button>
                  <Button variant="outline" onClick={() => window.open(`/elearning/courses/${course?.slug}`, '_blank')} disabled={!course?.slug} className="w-full border-white/20 text-white hover:bg-white/10">
                    <Eye className="mr-2" size={16} />
                    Prévisualiser
                  </Button>
                  <Button onClick={saveAllChanges} className="w-full bg-blue-600 hover:bg-blue-700 text-white relative" disabled={isSaving}>
                    {isSaving ? (
                      <><div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div><span>Enregistrement...</span></>
                    ) : (
                      <><Save className="mr-2" size={16} /><span>Tout enregistrer</span></>
                    )}
                  </Button>
                  <Button 
                    onClick={() => navigate(`/admin/elearning/courses/edit/${courseId}`)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Edit className="mr-2" size={16} />
                    Éditer les infos du cours
                  </Button>
                  {lastSaved && <div className="text-center text-xs text-white/60 pt-2">Dernière sauvegarde: {lastSaved}</div>}
                </CardContent>
              </Card>

              {/* Guide */}
              <Card className="bg-black/20 border-white/10">
                <CardHeader><CardTitle className="text-white">Guide</CardTitle></CardHeader>
                <CardContent className="space-y-4 text-sm text-white/70">
                  <div>
                    <h4 className="font-medium text-white mb-2">Organisation</h4>
                    <p>Structurez votre cours en modules, puis ajoutez des leçons dans chaque module.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-2">Bonnes pratiques</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Commencez par une leçon d'introduction gratuite.</li>
                      <li>Alternez théorie et pratique.</li>
                      <li>Gardez les leçons courtes (5-15 min).</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Quiz Builder */}
      {selectedLessonForQuiz && (
        <QuizBuilder
          lessonId={selectedLessonForQuiz}
          isOpen={isQuizBuilderOpen}
          onClose={closeQuizBuilder}
          onSave={() => {
            // Optionnel : recharger les données du cours si nécessaire
            toast({
              title: 'Quiz sauvegardé',
              description: 'Le quiz a été configuré avec succès pour cette leçon',
            });
          }}
        />
      )}
      
      {/* Sélecteur de média pour les vidéos */}
      <MediaSelector
        isOpen={showVideoSelector}
        onClose={() => setShowVideoSelector(false)}
        onSelect={(url) => {
          if (typeof url === 'string') {
            setLessonForm({...lessonForm, video_url: url});
            setShowVideoSelector(false);
          }
        }}
        selectedUrl={lessonForm.video_url || ''}
        title="Sélectionner une vidéo"
        allowUpload={true}
      />
      
      {/* Sélecteur de média pour les fichiers */}
      <MediaSelector
        isOpen={showFileSelector}
        onClose={() => setShowFileSelector(false)}
        onSelect={(url) => {
          if (typeof url === 'string') {
            const fileName = url.split('/').pop() || 'fichier';
            setLessonForm({...lessonForm, file_url: url, file_name: fileName});
            setShowFileSelector(false);
          }
        }}
        selectedUrl={lessonForm.file_url || ''}
        title="Sélectionner un fichier"
        allowUpload={true}
      />
    </AdminLayout>
  );
};

export default CourseBuilderPage;
